import { CloudUpload as UploadIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Avatar,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useToast } from "contexts/ToastContext";
import { resizeAndCropImage, validateFile } from "helpers/imageProcessing";
import { useCallback, useState } from "react";

interface UpdateLogoModalProps {
  readonly open: boolean;
  readonly currentLogo?: string;
  readonly practiceName: string;
  readonly onClose: () => void;
  readonly onUpdate: (file: File) => Promise<void>;
}

function UpdateLogoModal({
  open,
  currentLogo,
  practiceName,
  onClose,
  onUpdate,
}: UpdateLogoModalProps) {
  const { showError } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    onClose();
  }, [previewUrl, onClose]);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        showError("Invalid File", validationError);
        return;
      }

      try {
        setIsProcessing(true);

        // Resize and crop image to 320x320
        const processedFile = await resizeAndCropImage(file);

        // Create preview URL
        const preview = URL.createObjectURL(processedFile);
        setPreviewUrl(preview);
        setSelectedFile(processedFile);
      } catch (error) {
        console.error("Error processing image:", error);
        showError(
          "Processing Error",
          "Failed to process image. Please try again."
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [showError]
  );

  const handleUpdate = useCallback(async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      await onUpdate(selectedFile);
      handleClose();
    } catch (error) {
      console.error("Error updating logo:", error);
      // Error toast is handled by parent component
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, onUpdate, handleClose]);

  const displayLogo = previewUrl || currentLogo;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Practice Logo</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 3,
            gap: 3,
          }}
        >
          {/* Logo Preview */}
          <Avatar
            src={displayLogo}
            alt={practiceName}
            sx={{
              width: 160,
              height: 160,
              fontSize: "3rem",
            }}
          >
            {practiceName?.charAt(0).toUpperCase() || "P"}
          </Avatar>

          {/* Upload Button */}
          <Button
            variant="outlined"
            component="label"
            startIcon={
              isProcessing ? <CircularProgress size={20} /> : <UploadIcon />
            }
            disabled={isProcessing || isUploading}
            sx={{ mb: 1 }}
          >
            {selectedFile ? "Choose Different Image" : "Upload Logo"}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
            />
          </Button>

          {/* Instructions */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textAlign: "center",
              lineHeight: 1.6,
              maxWidth: 400,
            }}
          >
            Image will be resized and cropped to 320x320 pixels (center crop).
            <br />
            Maximum file size: 5MB. Supported formats: JPG, PNG, GIF, WebP.
          </Typography>

          {/* Show update button if image is selected */}
          {selectedFile && !isProcessing && (
            <Typography
              variant="body2"
              color="primary"
              sx={{
                fontWeight: 500,
                mt: 1,
              }}
            >
              New logo ready to apply
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={!selectedFile || isProcessing || isUploading}
          startIcon={isUploading ? <CircularProgress size={20} /> : null}
        >
          {isUploading ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UpdateLogoModal;
